import { Heart, Send, MoreHorizontal } from 'lucide-react';
import { FaInstagram } from 'react-icons/fa';
import { PreviewCardShell } from './PreviewCardShell';

interface InstagramStoryMockupProps {
  username?: string;
  hook?: string;
  views?: string;
  storyCount?: string;
  currentStory?: string;
  previewLabel?: string;
}

export const InstagramStoryMockup = ({
  username = '@username',
  hook,
  views = '0',
  storyCount = '3',
  currentStory = '1',
  previewLabel = 'Instagram Story Preview',
}: InstagramStoryMockupProps) => {
  const totalStories = parseInt(storyCount) || 3;
  const current = parseInt(currentStory) || 1;

  return (
    <PreviewCardShell
      className="mx-auto mb-3 w-full max-w-sm"
      badgeClassName="bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737]"
      badge={<FaInstagram className="h-4 w-4 text-white" />}
      label={previewLabel}
    >
      {/* Story container - 9:16 aspect ratio */}
      <div className="relative bg-gradient-to-br from-purple-900 to-pink-900" style={{ aspectRatio: '9/16' }}>
        {/* Progress bars at top */}
        <div className="absolute top-0 left-0 right-0 flex gap-1 p-2 z-20">
          {Array.from({ length: totalStories }).map((_, idx) => (
            <div
              key={idx}
              className="h-0.5 flex-1 bg-white/30 rounded-full overflow-hidden"
            >
              {idx + 1 === current && (
                <div className="h-full bg-white w-1/2" />
              )}
              {idx + 1 < current && (
                <div className="h-full bg-white w-full" />
              )}
            </div>
          ))}
        </div>

        {/* Story header */}
        <div className="absolute top-4 left-0 right-0 flex items-center justify-between px-3 z-20">
          <div className="flex items-center gap-2">
            {/* Profile pic */}
            <div className="w-8 h-8 rounded-full p-0.5 bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]">
              <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-400 to-pink-400" />
            </div>
            <span className="text-white font-semibold text-sm drop-shadow-lg">{username}</span>
            <span className="text-white/80 text-xs">now</span>
          </div>
          <MoreHorizontal className="w-5 h-5 text-white" />
        </div>

        {/* Story content */}
        <div className="absolute inset-0 flex items-center justify-center px-6">
          {hook ? (
            <p className="text-white text-center text-2xl font-bold drop-shadow-lg line-clamp-4">
              {hook}
            </p>
          ) : (
            <div className="w-24 h-24 border-4 border-white/30 rounded-lg" />
          )}
        </div>

        {/* Bottom interaction area */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4 z-20">
          {/* Reply input */}
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 border border-white/40 rounded-full px-4 py-2 bg-white/10 backdrop-blur-sm">
              <span className="text-white/60 text-sm">Send message</span>
            </div>
            <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <Send className="w-5 h-5 text-white" />
            </div>
          </div>

          {/* Views count */}
          <div className="text-white/80 text-xs text-center">
            👁️ {views} views
          </div>
        </div>
      </div>
    </PreviewCardShell>
  );
};
